<template>
    <div class="admin-content-container">
        <div class="flex justify-between items-center mb-6">
            <h2 class="font-header text-[3rem] font-bold text-primary">About Us</h2>
        </div>

        <div v-if="loading" class="p-4 text-center">
            <Loader size="medium" />
        </div>

        <div v-else class="space-y-6">
            <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="font-header text-[1.5rem] font-bold mb-4">Page Description</h3>
                <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600 mb-4">Edit the description that appears
                    at the top of the About Us page.</p>
                <div class="space-y-4">
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Description</label>
                        <div class="border border-gray-300 rounded-md">
                            <RichTextEditor ref="descriptionEditor" :content="formData.description"
                                @update:content="formData.description = $event"
                                placeholder="Write your content here..." />
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="font-header text-[1.5rem] font-bold mb-4">Mission</h3>
                <div class="space-y-4">
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Mission Content</label>
                        <div class="border border-gray-300 rounded-md">
                            <RichTextEditor ref="missionEditor" :content="formData.mission"
                                @update:content="formData.mission = $event" placeholder="Write your content here..." />
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="font-header text-[1.5rem] font-bold mb-4">Vision</h3>
                <div class="space-y-4">
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Vision Content</label>
                        <div class="border border-gray-300 rounded-md">
                            <RichTextEditor ref="visionEditor" :content="formData.vision"
                                @update:content="formData.vision = $event" placeholder="Write your content here..." />
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white p-6 rounded-lg shadow-md">
                <h3 class="font-header text-[1.5rem] font-bold mb-4">Values</h3>
                <div class="space-y-4">
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Values Introduction</label>
                        <div class="border border-gray-300 rounded-md">
                            <RichTextEditor ref="valuesIntroEditor" :content="formData.valuesIntro"
                                @update:content="formData.valuesIntro = $event"
                                placeholder="Write your content here..." />
                        </div>
                    </div>
                    <div>
                        <div class="flex justify-between items-center mb-2">
                            <label class="block font-body text-[1.125rem] leading-[1.75]">Value Items</label>
                            <button type="button" @click="addValue"
                                class="px-3 py-1 text-sm bg-green-700 text-white rounded hover:bg-green-800 font-marketing">
                                Add Value
                            </button>
                        </div>
                        <div v-for="(value, index) in formData.values" :key="index"
                            class="mb-4 p-4 border border-gray-300 rounded-md">
                            <div class="flex justify-between items-center mb-2">
                                <label class="block font-body text-[1.125rem] leading-[1.75]">Value {{ index + 1
                                }}</label>
                                <button type="button" @click="removeValue(index)"
                                    class="text-red-600 hover:text-red-700 text-sm font-marketing">Remove</button>
                            </div>
                            <input v-model="value.title" type="text" placeholder="Value title (e.g., Respect)"
                                class="w-full px-3 py-2 mb-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                            <div class="border border-gray-300 rounded-md">
                                <RichTextEditor :ref="el => valueEditors[index] = el" :content="value.content"
                                    @update:content="value.content = $event" placeholder="Write your content here..." />
                            </div>
                        </div>
                        <p v-if="formData.values.length === 0"
                            class="font-body text-[1.125rem] leading-[1.75] text-gray-500">No values added. Click "Add
                            Value" to create one.</p>
                    </div>
                </div>
            </div>

            <div class="flex gap-2">
                <button @click="saveAbout" :disabled="saving"
                    class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ saving ? 'Saving...' : 'Save Changes' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { adminService } from '../../services/admin'
import { publicService } from '../../services/public'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import RichTextEditor from '../common/RichTextEditor.vue'
import { convertLegacyQuillContent } from '../../utils/quillConfig'

const { loading, setLoading } = useLoadingState(true)
const saving = ref(false)
const descriptionEditor = ref(null)
const missionEditor = ref(null)
const visionEditor = ref(null)
const valuesIntroEditor = ref(null)
const valueEditors = ref([])

const formData = ref({
    description: convertLegacyQuillContent(''),
    mission: convertLegacyQuillContent(''),
    vision: convertLegacyQuillContent(''),
    valuesIntro: convertLegacyQuillContent(''),
    values: []
})

const aboutPageId = ref(null)

const findSection = (sections, title) => {
    return sections.find(s => s.title.toLowerCase() === title.toLowerCase())
}

const loadAboutPage = async () => {
    setLoading(true)
    try {
        const page = await publicService.getPage('About Us')
        aboutPageId.value = page.id

        formData.value.description = convertLegacyQuillContent(page.description || '')

        const missionSection = findSection(page.sections || [], 'Our Mission')
        const visionSection = findSection(page.sections || [], 'Our Vision')
        const valuesSection = findSection(page.sections || [], 'Our Values')

        formData.value.mission = convertLegacyQuillContent(missionSection?.content || '')
        formData.value.vision = convertLegacyQuillContent(visionSection?.content || '')

        if (valuesSection) {
            const content = valuesSection.content || ''
            const firstValueIndex = content.indexOf('###')

            if (firstValueIndex > 0) {
                formData.value.valuesIntro = convertLegacyQuillContent(content.substring(0, firstValueIndex).trim())
            } else if (firstValueIndex === -1) {
                formData.value.valuesIntro = convertLegacyQuillContent(content.trim())
            } else {
                formData.value.valuesIntro = convertLegacyQuillContent('')
            }

            const valueItems = []
            const valuePattern = /###\s*(.+?)\n\n(.+?)(?=\n\n###|$)/gs
            let match
            while ((match = valuePattern.exec(content)) !== null) {
                valueItems.push({
                    title: match[1].trim(),
                    content: convertLegacyQuillContent(match[2].trim())
                })
            }

            formData.value.values = valueItems
        } else {
            formData.value.valuesIntro = convertLegacyQuillContent('')
            formData.value.values = []
        }

        await nextTick()
        if (descriptionEditor.value) {
            descriptionEditor.value.setHTML(formData.value.description || '')
        }
        if (missionEditor.value) {
            missionEditor.value.setHTML(formData.value.mission || '')
        }
        if (visionEditor.value) {
            visionEditor.value.setHTML(formData.value.vision || '')
        }
        if (valuesIntroEditor.value) {
            valuesIntroEditor.value.setHTML(formData.value.valuesIntro || '')
        }
        valueEditors.value = []
    } catch (error) {
        console.error('Failed to load About page:', error)
        aboutPageId.value = null
        formData.value.description = convertLegacyQuillContent('')
        formData.value.mission = convertLegacyQuillContent('')
        formData.value.vision = convertLegacyQuillContent('')
        formData.value.valuesIntro = convertLegacyQuillContent('')
        formData.value.values = []
    } finally {
        setLoading(false)
    }
}

const addValue = () => {
    formData.value.values.push({ title: '', content: convertLegacyQuillContent('') })
    valueEditors.value.push(null)
}

const removeValue = (index) => {
    formData.value.values.splice(index, 1)
    valueEditors.value.splice(index, 1)
}

const saveAbout = async () => {
    try {
        saving.value = true

        const sections = []

        if (formData.value.mission.trim()) {
            sections.push({
                title: 'Our Mission',
                content: formData.value.mission.trim()
            })
        }

        if (formData.value.vision.trim()) {
            sections.push({
                title: 'Our Vision',
                content: formData.value.vision.trim()
            })
        }

        if (formData.value.valuesIntro.trim() || formData.value.values.length > 0) {
            let valuesContent = formData.value.valuesIntro.trim()

            if (formData.value.values.length > 0) {
                if (valuesContent) {
                    valuesContent += '\n\n'
                }
                valuesContent += formData.value.values.map(v => {
                    return `### ${v.title}\n\n${v.content}`
                }).join('\n\n')
            }

            if (valuesContent.trim()) {
                sections.push({
                    title: 'Our Values',
                    content: valuesContent.trim()
                })
            }
        }

        const data = {
            title: 'About Us',
            description: formData.value.description.trim() || null,
            sections: sections.length > 0 ? sections : null
        }

        if (aboutPageId.value) {
            await adminService.updatePage(aboutPageId.value, data)
        } else {
            const created = await adminService.createPage(data)
            aboutPageId.value = created.id
        }

        alert('About Us page saved successfully!')
        await loadAboutPage()
    } catch (error) {
        console.error('Failed to save About page:', error)
        alert('Failed to save About Us page. Please try again.')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    loadAboutPage()
})
</script>

<style scoped>
.admin-content-container {
    width: 100%;
    min-height: 100vh;
    background-color: #F6F4F1;
    padding: 2rem;
    box-sizing: border-box;
}

:deep(.ql-editor) {
    min-height: 150px;
    line-height: 1.75;
    color: #253439;
    direction: ltr !important;
    text-align: left !important;
    unicode-bidi: embed !important;
}

:deep(.ql-container) {
    font-family: 'Manrope', sans-serif;
    direction: ltr !important;
    unicode-bidi: embed !important;
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
}

:deep(.ql-toolbar) {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    background-color: #FAFAFA;
}

:deep(.ql-editor *) {
    direction: ltr !important;
}
</style>
